import type { ProductionLineResponse } from '@/storageAreas/api/models/storageAreaResponse';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export interface EditProductionLine {
  storageAreaId: string;
  productionLine: ProductionLineResponse;
}

export interface CopyProductionLine {
  productionLine: ProductionLineResponse;
}

export const useSelectedProductionLineStore = defineStore('selectedProductionLineStore', () => {
  const operation = ref<'create' | 'edit' | 'none'>('none');
  const model = ref<ProductionLineResponse>();

  const simulationEnabled = ref<boolean>(false);
  const simulationIntervalSeconds = ref<number | null>(null);
  const simulationOutputPlaceId = ref<string | null>(null);
  const simulationProductId = ref<string | null>(null);
  const simulationPiecesCount = ref<number | null>(null);
  const simulationPackagingId = ref<string | null>(null);
  const simulationPackageTypeId = ref<string | null>(null);
  const simulationLength = ref<number | null>(null);
  const simulationWidth = ref<number | null>(null);
  const simulationHeight = ref<number | null>(null);

  const changed = computed(() => {
    if (operation.value !== 'edit' || !model.value) return false;
    const m = model.value;
    return (
      simulationEnabled.value !== m.simulationEnabled ||
      simulationIntervalSeconds.value !== m.simulationIntervalSeconds ||
      simulationOutputPlaceId.value !== m.simulationOutputPlaceId ||
      simulationProductId.value !== m.simulationProductId ||
      simulationPiecesCount.value !== m.simulationPiecesCount ||
      simulationPackagingId.value !== m.simulationPackagingId ||
      simulationPackageTypeId.value !== m.simulationPackageTypeId ||
      simulationLength.value !== m.simulationLength ||
      simulationWidth.value !== m.simulationWidth ||
      simulationHeight.value !== m.simulationHeight
    );
  });

  function reset() {
    operation.value = 'none';
    model.value = undefined;
    simulationEnabled.value = false;
    simulationIntervalSeconds.value = null;
    simulationOutputPlaceId.value = null;
    simulationProductId.value = null;
    simulationPiecesCount.value = null;
    simulationPackagingId.value = null;
    simulationPackageTypeId.value = null;
    simulationLength.value = null;
    simulationWidth.value = null;
    simulationHeight.value = null;
  }

  function fill(source: ProductionLineResponse) {
    simulationEnabled.value = source.simulationEnabled;
    simulationIntervalSeconds.value = source.simulationIntervalSeconds;
    simulationOutputPlaceId.value = source.simulationOutputPlaceId;
    simulationProductId.value = source.simulationProductId;
    simulationPiecesCount.value = source.simulationPiecesCount;
    simulationPackagingId.value = source.simulationPackagingId;
    simulationPackageTypeId.value = source.simulationPackageTypeId;
    simulationLength.value = source.simulationLength;
    simulationWidth.value = source.simulationWidth;
    simulationHeight.value = source.simulationHeight;
  }

  function create() {
    reset();
    operation.value = 'create';
  }

  function edit(action: EditProductionLine) {
    reset();
    fill(action.productionLine);
    model.value = action.productionLine;
    operation.value = 'edit';
  }

  function copy(action: CopyProductionLine) {
    reset();
    fill(action.productionLine);
    operation.value = 'create';
  }

  return {
    operation,
    model,
    simulationEnabled,
    simulationIntervalSeconds,
    simulationOutputPlaceId,
    simulationProductId,
    simulationPiecesCount,
    simulationPackagingId,
    simulationPackageTypeId,
    simulationLength,
    simulationWidth,
    simulationHeight,
    changed,
    reset,
    create,
    edit,
    copy,
  };
});
